import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Classes, Menu, MenuItem, Popover } from '@el8/vital';
import { logout as logoutAction, getActivePatient } from 'modules/patient';
import { updateSession as updateSessionAction } from 'modules/session';
import { State, HydratedPatientInfo, SessionState, Dispatch, PatientChartInfo } from 'types';
import { getFullNameFromPatientChart } from 'utils/patient';

import styles from './Profile.less';

type StateProps = {
  activeChartId: number;
  patient: HydratedPatientInfo;
};

type DispatchProps = {
  logout: () => void;
  updateSession: (newSessionData: Partial<SessionState>) => void;
};

type Props = StateProps & DispatchProps;

const mapStateToProps = (state: State): StateProps => ({
  activeChartId: state.session.activeChartId,
  patient: getActivePatient(state),
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  logout(): void {
    dispatch(logoutAction());
  },
  updateSession(newSessionData: Partial<SessionState>): void {
    dispatch(updateSessionAction(newSessionData));
  },
});

const Profile: React.FC<Props> = ({ activeChartId, logout, patient, updateSession }) => {
  const activeChart = useMemo(
    (): PatientChartInfo =>
      patient.charts.length > 1
        ? patient.charts.find((chart): boolean => chart.id === activeChartId)
        : patient.charts[0],
    [patient.charts, activeChartId],
  );

  /**
   * Special handling for the edge case
   * If passport user books an appointment for the chart that does not belong to
   * the current practice (the practice they are booking an appointment with) on
   * the backend, we create a new chart within the current practice that has the
   * same demographic as the original chart.
   * Next time when we fetch patient with charts for the current practice, the original
   * chart will be missing in the server response. Instead, there will be a newly
   * created chart.
   * When this request updates the store, we might end up in a situation, where
   * `activeChartId` in the `session` reducer points to the original chart, that
   * is missing in the store.
   * After the patient was fetched, the `activeChartId` in `session` reducer is also
   * updated to point to the actual chart, but it happens only on the next tick.
   * And before that happens, the `activeChart` might be null. Therefore we need this
   * guard to not to crash the app.
   */
  if (!activeChart) return null;

  return (
    <Popover padded={false}>
      <div className={styles.profile}>
        {activeChart.actualName
          ? activeChart.actualName[0]
          : `${activeChart.firstName[0]}${activeChart.lastName[0]}`}
      </div>
      <Menu>
        {patient.charts.length > 1 &&
          patient.charts.map(
            (chart): JSX.Element => (
              <MenuItem
                isActive={chart.id === activeChartId}
                className={Classes.POPOVER_DISMISS}
                key={chart.id}
                onClick={(): void => updateSession({ activeChartId: chart.id })}
              >
                {getFullNameFromPatientChart(chart)}
              </MenuItem>
            ),
          )}
        <MenuItem
          className={Classes.POPOVER_DISMISS}
          href={`${window.el8Globals.PASSPORT_BASE_URL}/passport`}
          target="_blank"
        >
          Take me to passport
        </MenuItem>
        <div className={styles.divider} />
        <MenuItem className={Classes.POPOVER_DISMISS} onClick={logout}>
          Log out
        </MenuItem>
      </Menu>
    </Popover>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Profile);
